/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import AppDateInput from "../../Inputs/AppDate";
import AppButton from "../../Inputs/AppButton";
import { ButtonType, TextInputType } from "../../../enums/componentEnums";
import * as Yup from "yup";
import useCustomFormik from "../../../customHooks/useFormik";
import { usePostData } from "../../../customHooks/usePostData";
import { useFetchData } from "../../../customHooks/useGetDashboardData";
import { enqueueSnackbar } from "notistack";
import Loader2 from "../../feedBacks/loader2";
import HierarchicalDeviceSelector from "../../Charts/HierarchicalDeviceSelector";
import { useUtils } from "../../../customHooks/useUtils";

function RawData({ company_id }: { company_id: string }) {
  const { EXPORT_PARAMETERS, t } = useUtils();
  const [selectedParameters, setSelectedParameters] = useState<string[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([
    "Voltage",
  ]);

  // Validation schema for export form
  const validationSchema = Yup.object().shape({
    startDate: Yup.date().required("Start date is required"),
    endDate: Yup.date()
      .required("End date is required")
      .min(Yup.ref("startDate"), "End date must be after start date"),
    gateway_serials: Yup.array()
      .of(Yup.string())
      .required("Select at least one device"),
  });

  // Fetch device hierarchy data
  const {
    data: hierarchyData,
    isLoading: hierarchyLoading,
    error: hierarchyError,
  }: any = useFetchData(
    ["/classifications", "fetchAssetClassifications", company_id],
    "/classifications/",
    {
      company_id,
    },
    // @ts-ignore
    {}
  );

  // Export mutation hook
  const exportMutation = usePostData<any>(
    "/bulk-export",
    "POST",
    {
      responseType: "blob", 
      onSuccess: (response) => {
        try {
          if (!response) {
            throw new Error("No data received from the server");
          }

          // Create a blob from the response
          const blob =
            response instanceof Blob
              ? response
              : new Blob([response], {
                  type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });

          // Check if the blob is valid (not empty)
          if (blob.size === 0) {
            throw new Error("Generated file is empty");
          }

          // Create object URL for the blob
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;

          let fileName;
          const contentDisposition = response.headers?.["content-disposition"];

          if (contentDisposition) {
            const fileNameMatch = contentDisposition.match(/filename="(.+?)"/);
            if (fileNameMatch?.length === 2) {
              fileName = fileNameMatch[1];
            }
          }

          // Use fallback filename if not available from headers
          if (!fileName) {
            const paramNames =
              selectedParameters.slice(0, 2).join("_").substring(0, 30) +
              (selectedParameters.length > 2 ? "_more" : "");
            fileName = `smartdevice_data_${paramNames}_${formik.values.startDate}_to_${formik.values.endDate}.xlsx`;
          }

          a.download = fileName;
          document.body.appendChild(a);
          a.click();

          // Clean up
          setTimeout(() => {
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
          }, 100);
          enqueueSnackbar("Exported file downloaded successfully", {
            variant: "success",
          });
          formik.setSubmitting(false);
          formik.resetForm();
          setSelectedParameters([]);
        } catch (error) {
          console.error("Error processing export file:", error);
          enqueueSnackbar(
            "There was an error processing the exported file. Please try again.",
            { variant: "error" }
          );
          formik.setSubmitting(false);
        }
      },
      onError: (error) => {
        console.error("Export failed:", error);
        enqueueSnackbar(
          error.response?.data?.error ||
            "Export failed. Please try again with a smaller date range or fewer parameters.",
          { variant: "error" }
        );
        formik.setSubmitting(false);
      },
    },
    {
      company_id,
    }
  );

  // Formik for handling form state and submission
  const formik = useCustomFormik({
    initialValues: {
      startDate: "",
      endDate: "",
      gateway_serials: [],
    },
    validationSchema,
    onSubmit: (values) => {
      if (selectedParameters.length === 0) {
        enqueueSnackbar("Please select at least one parameter to export", {
          variant: "error",
        });
        return;
      }

      // Prepare payload for export
      exportMutation.mutate({
        start_date: values.startDate,
        end_date: values.endDate,
        gateway_serials: values.gateway_serials,
        parameters: selectedParameters,
      });
    },
  });

  // Toggle parameter selection
  const handleParameterToggle = (value: string) => {
    setSelectedParameters((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value]
    );
  };

  // Toggle category expansion
  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((item) => item !== category)
        : [...prev, category]
    );
  };

  // Handle selecting all parameters in a category
  const handleSelectAllInCategory = (category: string) => {
    const categoryParameters =
      EXPORT_PARAMETERS.find(
        (cat) => cat.category === category
      )?.parameters.map((p) => p.value) || [];

    const allSelected = categoryParameters.every((param) =>
      selectedParameters.includes(param)
    );

    setSelectedParameters((prev) =>
      allSelected
        ? prev.filter((item) => !categoryParameters.includes(item))
        : [...new Set([...prev, ...categoryParameters])]
    );
  };

  // Handle device selection change now using the hierarchical component
  const handleDeviceSelectionChange = (selectedSerials: string[]) => {
    formik.setFieldValue("gateway_serials", selectedSerials);
  };

  return (
    <div className="w-full flex items-start justify-start">
      <div className="flex flex-col items-start justify-center min-h-[60%] w-[70%] max-w-[550px] bg-[#FDFDFD] gap-4 p-8 rounded-[20px] border border-[#77777733]">
        <form
          onSubmit={formik.handleSubmit}
          className="flex flex-col w-full space-y-4 gap-6"
        >
          <div className="w-full sm:max-w-[250px] flex flex-col gap-6">
            <AppDateInput
              topLabel={t("filter.start_date")}
              name="startDate"
              value={formik.values.startDate}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.getFieldError("startDate") as string}
              type={TextInputType.DATE}
              className={"!text-md !border-primary-border"}
            />
            <AppDateInput
              name="endDate"
              topLabel={t("filter.end_date")}
              value={formik.values.endDate}
              onChange={formik.handleChange}
              error={formik.getFieldError("endDate") as string}
              type={TextInputType.DATE}
              className={"!text-md !border-primary-border"}
            />

            <div className="w-full">
              <label className="block text-md text-primary-blackMain font-semibold mb-1">
                {t("devices")}
              </label>
              {hierarchyLoading ? (
                <div className="">
                  <Loader2 />
                </div>
              ) : hierarchyError ? (
                <span className="text-red-500 text-sm font-semibold">
                  {hierarchyError}
                </span>
              ) : (
                <HierarchicalDeviceSelector
                  hierarchy={hierarchyData || []}
                  selectedDevices={formik.values.gateway_serials}
                  onChange={handleDeviceSelectionChange}
                />
              )}
              {formik.errors.gateway_serials &&
                formik.touched.gateway_serials && (
                  <p className="text-red-500 text-xs mt-1">
                    {formik.errors.gateway_serials as string}
                  </p>
                )}
            </div>
          </div>

          <div className="w-full">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-md text-primary-blackMain font-semibold">
                Select details to export
                {selectedParameters.length > 0 &&
                  ` (${selectedParameters.length})`}
              </h3>

              {selectedParameters.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedParameters([])}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Clear all
                </button>
              )}
            </div>

            <div className="mt-2 divide-y border rounded-md max-h-[400px] overflow-y-auto">
              {EXPORT_PARAMETERS.map((category) => (
                <div key={category.category} className="p-2">
                  <div
                    className="flex items-center justify-between cursor-pointer p-1 hover:bg-gray-50"
                    onClick={() => toggleCategory(category.category)}
                  >
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        className="mr-2 h-4 w-4"
                        checked={
                          category.parameters.length > 0 &&
                          category.parameters.every((p) =>
                            selectedParameters.includes(p.value)
                          )
                        }
                        onChange={() =>
                          handleSelectAllInCategory(category.category)
                        }
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="font-medium">{category.category}</span>
                    </div>
                    <span>
                      {expandedCategories.includes(category.category)
                        ? "−"
                        : "+"}
                    </span>
                  </div>

                  {expandedCategories.includes(category.category) && (
                    <div className="pl-6 mt-1 flex flex-col gap-1">
                      {category.parameters.map((param) => (
                        <label
                          key={param.value}
                          className="inline-flex items-center"
                        >
                          <input
                            type="checkbox"
                            checked={selectedParameters.includes(param.value)}
                            onChange={() => handleParameterToggle(param.value)}
                            className="form-checkbox h-4 w-4 text-indigo-600"
                          />
                          <span className="ml-2 text-sm">{param.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-4">
            <AppButton text={t("cancel")} type={ButtonType.SECONDARY} />
            <AppButton
              text={formik.isSubmitting ? "" : t("export_data")}
              type={ButtonType.PRIMARY}
              disabled={
                formik.isSubmitting ||
                selectedParameters.length === 0 ||
                !formik.values.gateway_serials.length ||
                !formik.values.startDate ||
                !formik.values.endDate
              }
              loader={{
                loading: formik.isSubmitting,
                height: 4,
                width: 4,
              }}
              handleClick={formik.handleSubmit}
            />
          </div>
        </form>
      </div>
    </div>
  );
}

export default RawData;
